import { useState, useEffect } from 'react';
import { useRegister, useVerifyRegistration } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Phone, User, GoogleLogo, LinkBreak } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import useAppStore from '@/stores/appStore';

export const Register = () => {
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';
  const interactiveColor = brandColorAccent === '#002FA7' ? '#4f46e5' : brandColorAccent;

  // Wizard steps: 'details' | 'otp'
  const [step, setStep] = useState('details');

  const [fullName, setFullName] = useState('');
  const [googleEmail, setGoogleEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneBody, setPhoneBody] = useState('');
  const [otp, setOtp] = useState('');
  const [googleId, setGoogleId] = useState(null);
  const [isGoogleLinked, setIsGoogleLinked] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const registerMutation = useRegister();
  const verifyMutation = useVerifyRegistration();

  // Cooldown countdown timer effect
  useEffect(() => {
    let interval = null;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleGoogleCredentialResponse = (response) => {
    const idToken = response.credential;
    if (!idToken) return;

    try {
      const base64Url = idToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const profile = JSON.parse(jsonPayload);

      setFullName(profile.name || '');
      setGoogleEmail(profile.email || '');
      setGoogleId(idToken);
      setIsGoogleLinked(true);
      toast.success(`Google account linked: ${profile.email}`);
    } catch (e) {
      console.error("Failed to parse Google ID token:", e);
      toast.error("Google authentication parsing failed.");
    }
  };

  // Google SSO button rendering
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || clientId.includes("your-google-client-id")) return;

    let resizeObserver = null;

    const renderGoogleButton = () => {
      if (!window.google) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCredentialResponse,
      });
      const btnDiv = document.getElementById('googleSignupBtn');
      if (btnDiv) {
        const containerWidth = btnDiv.offsetWidth;
        const targetWidth = containerWidth ? Math.max(200, Math.min(containerWidth, 400)) : 320;
        btnDiv.innerHTML = '';
        window.google.accounts.id.renderButton(btnDiv, {
          theme: 'filled_black',
          size: 'large',
          width: targetWidth,
        });
      }
    };

    const setupResizeObserver = () => {
      const btnDiv = document.getElementById('googleSignupBtn');
      if (btnDiv && window.ResizeObserver) {
        let lastWidth = btnDiv.offsetWidth;
        resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            const width = entry.contentRect.width;
            if (Math.abs(width - lastWidth) > 10) {
              lastWidth = width;
              renderGoogleButton();
            }
          }
        });
        resizeObserver.observe(btnDiv);
      }
    };

    let script = document.getElementById('google-jssdk');
    if (!script) {
      script = document.createElement('script');
      script.id = 'google-jssdk';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        renderGoogleButton();
        setupResizeObserver();
      };
      document.body.appendChild(script);
    } else {
      if (window.google) {
        renderGoogleButton();
        setupResizeObserver();
      } else {
        const oldOnload = script.onload;
        script.onload = (e) => {
          if (oldOnload) oldOnload(e);
          renderGoogleButton();
          setupResizeObserver();
        };
      }
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleGoogleSignupFallback = () => {
    const mockEmail = prompt("Enter mock username for Google Sign Up:", "john");
    if (!mockEmail) return;

    const email = `${mockEmail}@gmail.com`;
    const name = mockEmail.charAt(0).toUpperCase() + mockEmail.slice(1);

    setFullName(name);
    setGoogleEmail(email);
    setGoogleId(`mock_${mockEmail}`);
    setIsGoogleLinked(true);
    toast.success(`Mock Google account linked: ${email}`);
  };

  const handleUnlinkGoogle = () => {
    setFullName('');
    setGoogleEmail('');
    setGoogleId(null);
    setIsGoogleLinked(false);
    toast.info('Google account unlinked.');
  };

  const isValidPhone = (number) => {
    return /^\d{10}$/.test(number);
  };

  const handleRegisterDetailsSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) {
      toast.error('Please enter your Full Name');
      return;
    }
    if (!isValidPhone(phoneBody)) {
      toast.error('Please enter a valid 10-digit WhatsApp number');
      return;
    }

    const fullPhone = `${countryCode}${phoneBody}`;
    try {
      const payload = {
        full_name: fullName.trim(),
        phone: fullPhone,
        google_id: googleId,
      };
      await registerMutation.mutateAsync(payload);
      toast.success('Verification OTP sent via WhatsApp!');
      setResendCooldown(60);
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    const fullPhone = `${countryCode}${phoneBody}`;
    try {
      const payload = {
        full_name: fullName.trim(),
        phone: fullPhone,
        google_id: googleId,
      };
      await registerMutation.mutateAsync(payload);
      toast.success('New OTP sent via WhatsApp!');
      setResendCooldown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Resend failed');
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    const fullPhone = `${countryCode}${phoneBody}`;
    try {
      await verifyMutation.mutateAsync({ phone: fullPhone, otp });
      toast.success('Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      setOtp('');
    }
  };

  const companyName = tenantConfig?.company_name || 'KWICC';
  const logoUrl = tenantConfig?.logo_url;

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden px-4"
      style={{
        background: `linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)`,
      }}
    >
      {/* Subtle Decorative Orbs */}
      <div 
        className="absolute top-[-15%] left-[-10%] w-[50%] h-[50%] rounded-full blur-[160px]" 
        style={{ backgroundColor: brandColorAccent, opacity: 0.06 }}
      />
      <div className="absolute bottom-[-15%] right-[-10%] w-[50%] h-[50%] rounded-full blur-[160px]" style={{ backgroundColor: '#60a5fa', opacity: 0.05 }} />
      
      <div className="relative z-10 w-full max-w-md my-8">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <h1 className="text-4xl font-black text-slate-900 tracking-tight uppercase">KWICC</h1>
            {logoUrl && (
              <>
                <div className="h-6 w-[2px] bg-slate-300" />
                <img src={logoUrl} alt={companyName} className="h-10 object-contain" />
              </>
            )}
          </div>
          <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mt-1">{companyName} Customer Registration</p>
        </div>

        <Card className="p-8 bg-white border border-slate-200 text-slate-900 shadow-[0_20px_50px_rgba(0,0,0,0.06)] rounded-xl relative overflow-hidden">
          
          {/* Stepper indicator */}
          <div className="flex justify-between items-center gap-1.5 mb-6">
            <div className={`h-1 flex-1 rounded-full transition-all duration-300 ${step === 'details' ? 'bg-primary' : 'bg-slate-200'}`} />
            <div className={`h-1 flex-1 rounded-full transition-all duration-300 ${step === 'otp' ? 'bg-primary' : 'bg-slate-200'}`} />
          </div>

          {/* STEP 1: Details (Name & Phone) */}
          {step === 'details' && (
            <form onSubmit={handleRegisterDetailsSubmit} className="space-y-5">
              <div className="text-center space-y-1 mb-4">
                <h2 className="text-xl font-bold text-slate-900">Create your Account</h2>
                <p className="text-xs text-slate-500">Sign up using your phone number or link with Google</p>
              </div>

              {isGoogleLinked ? (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md flex items-center justify-between text-xs text-emerald-700">
                  <div className="flex items-center gap-2">
                    <GoogleLogo size={18} weight="fill" className="text-emerald-600" />
                    <span>Linked: <strong>{googleEmail}</strong></span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleUnlinkGoogle}
                    className="h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border border-red-200"
                  >
                    <LinkBreak size={14} />
                    Unlink
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {process.env.REACT_APP_GOOGLE_CLIENT_ID && 
                   !process.env.REACT_APP_GOOGLE_CLIENT_ID.includes("your-google-client-id") &&
                   window.location.hostname !== "localhost" &&
                   window.location.hostname !== "127.0.0.1" ? (
                    <div id="googleSignupBtn" className="w-full flex justify-center min-h-[44px]"></div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGoogleSignupFallback}
                      className="w-full h-12 font-bold flex items-center justify-center gap-3 bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 shadow-sm rounded-md"
                    >
                      <GoogleLogo size={20} weight="bold" style={{ color: '#4285F4' }} />
                      Sign up with Google
                    </Button>
                  )}

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-slate-400 font-bold">Or</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Full Name input */}
              <div className="space-y-1.5">
                <Label htmlFor="fullName" className="text-xs text-slate-600 uppercase tracking-wider font-bold">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="Enter full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="pl-10 h-12 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-primary"
                    required
                  />
                </div>
              </div>

              {/* Phone Number (WhatsApp) */}
              <div className="space-y-2">
                <Label htmlFor="phoneBody" className="text-xs text-slate-600 uppercase tracking-wider font-bold">Phone Number (WhatsApp)</Label>
                <div className="flex gap-2">
                  <div className="w-[100px]">
                    <Select value={countryCode} onValueChange={setCountryCode}>
                      <SelectTrigger className="h-12 border-slate-300 bg-white text-slate-900 focus:border-primary">
                        <SelectValue placeholder="+91" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200 text-slate-900">
                        <SelectItem value="+91">🇮🇳 +91</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                    <Input
                      id="phoneBody"
                      type="tel"
                      placeholder="Enter 10-digit number"
                      value={phoneBody}
                      onChange={(e) => setPhoneBody(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="pl-10 h-12 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-primary font-mono text-base"
                      required
                      maxLength={10}
                    />
                  </div>
                </div>
                {phoneBody && !isValidPhone(phoneBody) && (
                  <p className="text-[11px] text-red-500 font-semibold mt-1">
                    Please enter a valid 10-digit phone number.
                  </p>
                )}
                {phoneBody && isValidPhone(phoneBody) && (
                  <p className="text-[11px] text-green-600 font-semibold mt-1">
                    ✓ Valid 10-digit phone number.
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-12 font-semibold text-white hover:opacity-90 mt-4"
                style={{ backgroundColor: interactiveColor }}
                disabled={registerMutation.isPending || !fullName.trim() || !isValidPhone(phoneBody)}
              >
                {registerMutation.isPending ? 'Sending OTP...' : 'Send OTP'}
              </Button>
            </form>
          )}

          {/* STEP 2: OTP Verification */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <div className="space-y-1">
                <h2 className="text-lg font-bold text-slate-900">Enter OTP</h2>
                <p className="text-xs text-slate-500">Verify the verification code sent to your WhatsApp</p>
              </div>

              <div className="space-y-4">
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between">
                  <div>
                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Verification Phone</span>
                    <span className="text-sm font-semibold font-mono text-slate-800">{countryCode} {phoneBody}</span>
                  </div>
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setStep('details')}
                    className="p-0 h-auto text-xs hover:underline font-bold"
                    style={{ color: interactiveColor }}
                  >
                    Edit Details
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-slate-600 uppercase tracking-wider font-bold block text-center mb-1">Enter 6-Digit OTP</Label>
                  <div className="flex justify-center">
                    <InputOTP 
                      maxLength={6} 
                      value={otp} 
                      onChange={setOtp}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                        <InputOTPSlot index={1} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                        <InputOTPSlot index={2} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                        <InputOTPSlot index={3} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                        <InputOTPSlot index={4} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                        <InputOTPSlot index={5} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <p className="text-[11px] text-slate-500 text-center mt-2">
                    Check your WhatsApp account for the 6-digit confirmation pin.
                  </p>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <Button
                  type="submit"
                  className="w-full h-12 font-semibold text-white hover:opacity-90"
                  style={{ backgroundColor: interactiveColor }}
                  disabled={verifyMutation.isPending || otp.length !== 6}
                >
                  {verifyMutation.isPending ? 'Verifying...' : 'Verify & Complete Signup'}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResendOtp}
                  className="w-full h-11 font-semibold border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                  disabled={resendCooldown > 0 || registerMutation.isPending}
                >
                  {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend WhatsApp OTP'}
                </Button>
              </div>
            </form>
          )}

          <div className="text-center mt-6 pt-4 border-t border-slate-200">
            <span className="text-xs text-slate-500">Already have an account? </span>
            <Link to="/login" className="text-xs font-bold hover:underline" style={{ color: interactiveColor }}>
              Log In
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};
